/**
 * Passwordless sign-in / sign-up. There's no password — entering an email
 * sends a magic link. New emails create an account on first verify, so
 * this single screen serves both "sign up" and "sign in".
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Logo } from '../components/Layout';
import { Alert, Button, Input } from '../components/ui';

export default function Login() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.startEmail(email.trim(), 'login');
      navigate('/auth/check-email', { state: { email: email.trim() } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Sign in to SwarmAI Hub" subtitle="We'll email you a secure sign-in link — no password needed.">
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Email address</label>
          <Input
            type="email"
            required
            autoFocus
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Sending…' : 'Send magic link'}
        </Button>
      </form>
      <p className="mt-6 text-center text-xs text-slate-500">
        New here? Entering your email creates your account automatically.
      </p>
      <p className="mt-2 text-center text-xs text-slate-400">
        <Link to="/" className="hover:text-slate-600">
          ← Back to catalogue
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">{children}</div>
      </div>
    </div>
  );
}
